import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityAssessor, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service for assessor data.
 */
@Injectable()
export class IdentityAssessorService {
    public static INCLUDE_TEAMS: string = 'teams';
    public static INCLUDE_SEASON_W_ORGANIZATION: string = 'season.organization';
    public static INCLUDE_USER_W_PERSON: string = 'user.person';

    private readonly identityApiBaseUrl: string;
    private cachedAssessorsMap: { [apiRequest: string]: PagedResponse; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = this.http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all assessors for the specified event
     */
    public getIndexListResponse(
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl);
    }

    /**
     * Get assessors by a url. This is used for paging through assessors since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(url: string, forceRefresh: boolean = false): Observable<PagedResponse> {
        if (!forceRefresh &&
            this.cachedAssessorsMap[url]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedAssessorsMap[url].clone());
        }
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const included: any[] = response.included;
                const json: any = response;

                const assessors: IdentityAssessor[] = [];
                for (const assessor of json.data) {
                    assessors.push((new IdentityAssessor()).deserialize(assessor, included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    assessors,
                );
            }));
    }

    /**
     * Get a single assessor's data
     */
    public get(identityAssessorId: string, include: string[] = null): Observable<IdentityAssessor> {
        const apiRequestUrl: string = this.getApiUrl(identityAssessorId, include);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => new IdentityAssessor().deserialize(response, response.included)));
    }

    /**
     * Add or update the assessor
     */
    public save(assessor: IdentityAssessor, includes: string[] = null): Observable<IdentityAssessor> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(assessor.id, includes);

        if (assessor.id) {
            // Update existing
            response = this.http.patch(apiRequestUrl, assessor.serialize());
        } else {
            // Add a new
            response = this.http.post(apiRequestUrl, assessor.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityAssessor()).deserialize(response, response.included)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified assessor.
     */
    public delete(identityAssessorId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityAssessorId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Add one or more teams to the given identity assessor
     *
     * @param {string} identityAssessorId The identity assessor id
     * @param {string[]} identityTeamIds The list of teams to link to the given assessor
     * @returns {Observable<IdentityAssessor>}
     */
    public addTeams(
        identityAssessorId: string,
        identityTeamIds: string[],
    ): Observable<boolean> {
        if (!identityTeamIds || !identityTeamIds.length) {
            return of(true);
        }

        let apiRequestUrl: string = this.getApiUrl(identityAssessorId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = {};
        json.data = [];

        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.post(apiRequestUrl, JSON.stringify(json), true, false)
            .pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Delete one or more teams linked the given identity assessor
     *
     * @param {string} identityAssessorId The identity assessor id
     * @param {string[]} identityTeamIds The list of teams to link to the given assessor
     * @returns {Observable<IdentityAssessor>}
     */
    public deleteTeams(identityAssessorId: string, identityTeamIds: string[]): Observable<boolean> {
        if (!identityTeamIds || !identityTeamIds.length) {
            return of(true);
        }

        let apiRequestUrl: string = this.getApiUrl(identityAssessorId, null, null);
        apiRequestUrl += '/relationships/teams';

        const json: any = {};
        json.data = [];
        identityTeamIds.forEach((identityTeamId: string) => {
            json.data.push({ type: 'team', id: identityTeamId });
        });

        return this.http.delete(apiRequestUrl, true, false, this.http.getHeadersForJson(), JSON.stringify(json))
            .pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(assessorId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/assessors/`;

        if (assessorId) {
            url += assessorId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedAssessorsMap = {};
    }
}
